import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { uploadFotoBene } from "../../api/fotobene";

interface FotoUploadProps {
  id_bene_contratto: number;
  onUploadSuccess: () => void;
  onClose?: () => void;
}

const FotoUploadComponent: React.FC<FotoUploadProps> = ({
  id_bene_contratto,
  onUploadSuccess,
  onClose,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelect = async (selectedFiles: FileList) => {
    const validFiles: File[] = [];
    const newPreviews: string[] = [];
    
    for (const file of Array.from(selectedFiles)) {
      if (!file.type.startsWith("image/")) {
        setError("Alcuni file non sono immagini valide");
        continue;
      }

      if (file.size > 10 * 1024 * 1024) {
        setError("Alcuni file superano i 10MB");
        continue;
      }

      validFiles.push(file);
      
      // Crea preview in modo sincrono
      const preview = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.readAsDataURL(file);
      });
      
      newPreviews.push(preview);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
      setPreviews((prev) => [...prev, ...newPreviews]);
      setError(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    try {
      // Carica tutte le foto in sequenza
      for (const file of files) {
        await uploadFotoBene(id_bene_contratto, file);
      }
      setFiles([]);
      setPreviews([]);
      onUploadSuccess();
    } catch (err: any) {
      setError(err.message || "Errore durante l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFiles([]);
    setPreviews([]);
    setError(null);
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Aggiungi Foto</Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {!previews.length ? (
        <Paper
          variant="outlined"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
            backgroundColor: isDragging ? "#f0f7ff" : "transparent",
            transition: "all 0.3s",
          }}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            style={{ display: "none" }}
            id="foto-upload-input"
            multiple
          />
          <label htmlFor="foto-upload-input" style={{ cursor: "pointer" }}>
            <CloudUploadIcon
              sx={{ fontSize: 64, color: isDragging ? "#1976d2" : "#ccc", mb: 2 }}
            />
            <Typography variant="body1" gutterBottom>
              Trascina immagini qui o clicca per selezionare
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Formati supportati: JPG, PNG, WEBP (Max 10MB per file)
            </Typography>
          </label>
        </Paper>
      ) : (
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: isDragging ? "2px dashed #1976d2" : "none",
            backgroundColor: isDragging ? "#f0f7ff" : "transparent",
            transition: "all 0.3s",
            p: isDragging ? 2 : 0,
            borderRadius: 1,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
            {previews.map((preview, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 1, position: "relative", width: 150 }}>
                <Box 
                  sx={{
                    width: "100%",
                    height: 120,
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                  <IconButton
                    sx={{ position: "absolute", top: 4, right: 4, bgcolor: "white", zIndex: 2 }}
                    onClick={() => handleRemoveFile(index)}
                    size="small"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
          
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {files.length} {files.length === 1 ? "foto selezionata" : "foto selezionate"}
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={handleClear} disabled={uploading}>
              Annulla
            </Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={
                uploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </Box>
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FotoUploadComponent;
